import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Link,
    IconButton,
    useMediaQuery,
    Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { showToast } from "../../../../../utils/toast";
import {
    adminGetTaxFormAPI,
    adminVerifyTaxFormAPI,
    adminRejectTaxFormAPI,
} from "../../../../../api/taxFormsApi";
import { Link as RouterLink } from "react-router-dom";
import { UniversalDropdown } from "../../../../elements/UniversalDropdow";
import SortedTableCell from "../../../../elements/table/SortedTableCell";
import { clsx } from 'clsx';
import { Check, Clock8, Download, Eye, RefreshCw, X } from "lucide-react";
import SearchField from "../SearchField";
import { DetailPopup } from "../DetailPopup";
import theme from "../../../../../theme";
import { DetailDialog } from "../DetailPopupMobile";

type TaxFormType = "W9" | "W8BEN";
type TaxFormStatus = "pending" | "verified" | "rejected";
type SortByOptions = "type" | "createdAt" | "submittedAt";
interface ITaxForm {
    _id: string;
    userId: string;
    user: {
        username: string
    },
    type: TaxFormType;
    status: TaxFormStatus;
    fileId?: string;
    mediaUrl?: string;
    originalName?: string;
    mimeType?: string;
    submittedAt?: string;
    verifiedAt?: string;
    verifiedBy?: string;
    rejectedAt?: string;
    rejectedBy?: string;
    rejectedNote?: string;
    userCountry?: string;
    createdAt?: string;
    updatedAt?: string;
}

const useStyles = makeStyles((theme) => ({
    container: { padding: 24, color: "#fff" },

    topBar: {
        display: "flex",
        gap: 12,
        alignItems: "center",
        marginBottom: 12
    },
    spacer: { flex: 1 },

    filters: {
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(140px, 1fr))",
        gap: 12,
        marginBottom: 16,
    },

    tableWrap: {
        background: "#141414",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.08)",
    },

    tableHeaderCell: {
        color: "#fff",
        fontWeight: 500,
        fontSize: 16,
        background: "#74747426",
        borderRight: "1px solid #74747426",
        borderBottom: 0,
        lineHeight: "normal",
        height: "auto",
        verticalAlign: "center",

        "&.active": {
            color: "#FF78AA",
        },
        "& .MuiOutlinedInput-inputAdornedStart": {
            background: "transparent",
            border: 0,
        },
    },

    tableBodyCell: {
        color: "#fff",
        border: "1px solid #74747426",
        fontSize: 14,
        lineHeight: "normal",
        height: "auto",
    },

    actionBtnRow: {
        position: "relative",
        whiteSpace: "nowrap",
        display: "flex",
        gap: 8,
        justifyContent: "flex-start",
        minHeight: "48px",
    },
    actionButtons: {
        textTransform: "none",
        borderRadius: 6,
        maxHeight: "34px",
        "& .MuiButton-label": {
            display: "flex",
            gap: 4
        },
        [theme.breakpoints.down("md")]: {
            maxWidth: 140,
            width: "stretch",
        },
    },
    verifyButton: {
        fontSize: "14px",
        backgroundColor: "#85F96B",
    },
    rejectButton: {
        color: "#fff",
        borderColor: "#FF4977",
        fontSize: "14px",
    },

    noBorder: { border: 0 },

    searchInput: {
        color: "#fff",
        marginRight: 8,
        cursor: "pointer",
    },

    textFieldRoot: {
        "& .MuiOutlinedInput-adornedStart": {
            maxWidth: "165px",
            paddingLeft: 8,
            marginRight: 12,
        },
        "& .MuiOutlinedInput-adornedStart, & .MuiInputBase-input": {
            background: "#131313",
            borderRadius: 4,
        },
    },

    inputRoot: { color: "#fff" },
    inputLabel: { color: "rgba(255,255,255,0.9)" },

    outlined: {
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.35)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.6)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
    },

    menuPaper: { background: "#121212", color: "#fff" },
    dialogPaper: { background: "#0f0f0f", color: "#fff" },

    paginationRoot: { color: "#fff", fontSize: "16px" },
    paginationCaption: { color: "rgba(255,255,255,0.8)", fontSize: "16px" },
    paginationSelectIcon: { color: "#fff", fontSize: "16px" },

    outlinedIconButton: {
        color: "#fff",
        border: "1px solid #74747480",
        borderRadius: 6,
        padding: 8,
        fontSize: 14,
    },

    exportPayoutsButton: {
        color: "#fff",
        fontSize: "20px",
        fontWeight: 600,
        border: "1px solid #FE5A97 !important",
        padding: "12px 20px",
        borderRadius: 6,
        [theme.breakpoints.down("sm")]: {
            fontSize: 12,
            padding: "9px 15px",
        },
    },

    previewBox: {
        display: "grid",
        placeItems: "center",
        width: "100%",
        height: "70vh",
        background: "#111",
    },
    previewImg: { maxWidth: "100%", maxHeight: "100%" },
    previewIframe: { width: "100%", height: "100%", border: 0 },

    ellipsisCell: {
        maxWidth: 300,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    userNameLink: {
        color: "white",
        textDecoration: "underline",
        textDecorationColor: "white",
        "&:hover": {
            color: "#FE5A97",
            textDecorationColor: "#FE5A97"
        }
    },

    //Mobile
    card: {
        padding: 8,
        backgroundColor: "#131313",
        color: "#fff",
        border: "1px solid #1F1F1F",
        marginBottom: 8,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 60%) minmax(100px, 40%)",
        gap: 8,
        margin: "8px 0"
    },
    field: {
        display: "flex",
        flexDirection: "column",
    },
    label: {
        fontWeight: 600,
        fontSize: 12,
        color: "#FFFFFF",
    },
    value: {
        fontSize: 12,
        color: "#FFFFFFCC",
    },
    uploadButton: {
        padding: "8px 23px"
    },
    buttonBox: {
        border: "1px solid #74747426",
        borderRadius: 4,
        width: "max-content",
        padding: "10px 8px",
        margin: "16px 0",
        "& .MuiButton-text": {
            fontSize: 12
        }
    },
    fileLink: { cursor: "pointer", color: "#fff", textDecoration: "underline", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleString() : "—");

type TaxFormsTableProps = {
    items: ITaxForm[],
    sortBy: string,
    setSortBy: (field: string) => void,
    sortDir: "asc" | "desc",
    setSortDir: (dir: "asc" | "desc") => void,
    previewFile: (item: ITaxForm) => void,
    setPage: (page: number) => void,
    load: (page?: number, limit?: number, creatorUsername?: string, filters?: { status?: TaxFormStatus, type?: TaxFormType }) => void,
    loading: boolean,
    limit: number,
    creatorUsername: string,
    setCreatorUsername: (username: string) => void,
    type: TaxFormType | "",
    setType: (type: "" | TaxFormType) => void,
    status: TaxFormStatus | "",
    setStatus: (status: TaxFormStatus | "") => void,
    applyFilters: () => void,
}

const TaxFormsTable = ({ items, sortBy, setSortBy, sortDir, setSortDir,
    previewFile, setPage, load, loading, limit, creatorUsername, setCreatorUsername,
    type, setType, status, setStatus, applyFilters }: TaxFormsTableProps) => {
    const classes = useStyles();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [showCreatorSearch, setShowCreatorSearch] = useState(false);

    // view meta dialog
    const [viewOpen, setViewOpen] = useState(false);
    const [viewItem, setViewItem] = useState<ITaxForm | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    const sortByOptions: { label: string; value: SortByOptions | "" }[] = [
        { label: "All", value: "" },
        { label: "Type", value: "type" },
        { label: "Created At", value: "createdAt" },
        { label: "Submitted At", value: "submittedAt" },
    ];

    const statusOptions: { label: string; value: TaxFormStatus | "" }[] = [
        { label: "All", value: "" },
        { label: "Pending", value: "pending" },
        { label: "Verified", value: "verified" },
        { label: "Rejected", value: "rejected" },
    ];

    const typeOptions: { label: string; value: TaxFormType | "" }[] = [
        { label: "All", value: "" },
        { label: "W-9 (US)", value: "W9" },
        { label: "W-8BEN (Non-US)", value: "W8BEN" },
    ];

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return ""
        return new Date(dateString).toLocaleString("uk-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const destinationFields = [
        { label: "Type", value: viewItem?.type ?? "-" },
        { label: "UserId", value: viewItem?.userId ?? "-" },
        { label: "Submitted", value: formatDate(viewItem?.submittedAt) ?? "-" },
    ]

    const changeSort = (field: string) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }

        setPage(0);
        load(0, limit);
    };


    const openView = async (id: string) => {
        setViewLoading(true);
        setViewOpen(true);
        try {
            const doc = (await adminGetTaxFormAPI(id)) as ITaxForm;
            setViewItem(doc);
        } catch (e) {
            console.error(e);
            showToast({ message: "Failed to open form", err: true });
        } finally {
            setViewLoading(false);
        }
    };

    // const applyFilters = () => {
    //     setPage(0);
    //     load(0, limit);
    // };

    const doVerify = async (id: string) => {
        if (!window.confirm("Verify this tax form?")) return;
        try {
            await adminVerifyTaxFormAPI(id);
            showToast({ message: "Form verified", success: true });
            setViewOpen(false);
            load();
        } catch (e) {
            console.error(e);
            showToast({ message: (e as any)?.message || "Verify failed", err: true });
        }
    };

    const doReject = async (id: string) => {
        const note = window.prompt("Rejection reason (note):");
        if (note === null) return;
        try {
            await adminRejectTaxFormAPI(id, { note: note || "" });
            showToast({ message: "Form rejected", success: true });
            setViewOpen(false);
            load();
        } catch (e) {
            console.error(e);
            showToast({ message: (e as any)?.message || "Reject failed", err: true });
        }
    };

    const handleCloseDropdown = <T extends "" | TaxFormStatus | TaxFormType>(
        field: "type" | "status",
        value: T,
        setValue: (val: T) => void
    ) => {
        if (value !== undefined && setValue !== undefined) {
            setValue(value);
            setPage(0);
            load(0, limit, creatorUsername, { [field]: value });
        }
    };

    const clearSearchInputInCollumn = () => {
        const cleared = "";
        setCreatorUsername(cleared);
        setShowCreatorSearch(false);
        setPage(0);
        load(0, limit, cleared);
    }

    const handleCloseSortByDropdown = (value?: string | "") => {
        if (value !== undefined) {
            changeSort(value);
            setPage(0);
            load(0, limit, creatorUsername);
        }
    };

    const StatusChip = ({ status }: { status: string }) => {
        let color = "#FFFFFF";
        let style = isMobile ? { margin: 0, height: 12, width: 12 } : { marginRight: 4 }
        let icon = <RefreshCw style={style} color={color} />

        if (status === "pending") {
            icon = <Clock8 style={style} color={color} />;
        }
        if (status === "verified") {
            color = "#85F96B";
            icon = <Check style={style} color={color} />;
        }
        if (status === "rejected") {
            color = "#FF4977";
            icon = <X style={style} color={color} />;
        }

        return (
            <Chip
                size="small"
                label={status.charAt(0).toUpperCase() + status.slice(1)}
                icon={icon}
                style={{ color, backgroundColor: "transparent", fontSize: isMobile ? 12 : 14 }}
            />
        );
    };

    if (isMobile) {
        return (
            <>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box className={classes.buttonBox}>
                        <UniversalDropdown
                            label="Status"
                            value={status}
                            options={statusOptions}
                            onChange={(val) => handleCloseDropdown("status", val, setStatus)} />
                    </Box>
                    <Box className={classes.buttonBox}>
                        <UniversalDropdown
                            label="Sort by"
                            value={sortBy}
                            options={sortByOptions}
                            onChange={handleCloseSortByDropdown} />
                    </Box>
                </Box>
                <div>
                    {items.map(row => {
                        const canOpenRow = !!(row.fileId || row.mediaUrl);
                        const style = {
                            backgroundColor: row.status === "verified" ? "#85F96B0D" : row.status === "rejected" ? "#FF49770D" : "",
                            borderColor: row.status === "verified" ? "#506846" : row.status === "rejected" ? "#72273A" : ""
                        }
                        return (
                            <Paper key={row._id} className={classes.card} style={style}>
                                {row.status !== "pending" && <div className={classes.value}><StatusChip status={row.status} /></div>}
                                <div className={classes.grid}>
                                    <div className={classes.field}>
                                        <span className={classes.label}>User name</span>
                                        <RouterLink to={"/" + row.user.username} className={clsx(classes.userNameLink, classes.value)}>@{row.user.username}</RouterLink>
                                    </div>
                                    <div className={classes.field}>
                                        <span className={classes.label}>Submitted:</span>
                                        <span className={classes.value}>{fmtDate(row.submittedAt)}</span>
                                    </div>
                                    <div className={classes.field}>
                                        <span className={classes.label}>File</span>
                                        {canOpenRow ? (
                                            <Link
                                                underline="always"
                                                onClick={() => previewFile(row)}
                                                className={classes.fileLink}
                                            >
                                                {row.originalName || row.mimeType
                                                    ? `${row.originalName || ""} (${row.mimeType || ""})`
                                                    : "—"}
                                            </Link>

                                        ) : (
                                            <Typography>File not available</Typography>
                                        )}
                                    </div>
                                    <div className={classes.field}>
                                        <span className={classes.label}>Type</span>
                                        <span className={classes.value}>{row.type}</span>
                                    </div>
                                </div>
                                <Box display="flex" justifyContent="flex-end" style={{ gap: 8, padding: "13px 0" }}>
                                    {row.status === "pending" && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => doReject(row._id)}
                                            className={clsx(classes.actionButtons, classes.rejectButton)}
                                        >
                                            <X size={16} /> Reject
                                        </Button>
                                    )}
                                    {row.status !== "verified" && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => doVerify(row._id)}
                                            className={clsx(classes.actionButtons, classes.verifyButton)}
                                        >
                                            <Check size={16} /> Verify
                                        </Button>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            setAnchorEl(e.currentTarget)
                                            openView(row._id)
                                        }}
                                        className={classes.outlinedIconButton}
                                    >
                                        <Eye size={16} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => openView(row._id)}
                                        className={classes.outlinedIconButton}
                                    >
                                        <Download size={16} />
                                    </IconButton>
                                </Box>

                            </Paper>

                        )
                    })}
                    {!loading && items.length === 0 && (
                        <Typography style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
                            No payouts found
                        </Typography>
                    )}
                </div>
                {/* <DetailReceipt open={showReceipt} onClose={() => setShowReceipt(false)} data={info} /> */}

                {/* View Destination */}
                <DetailDialog
                    open={viewOpen}
                    onClose={() => setViewOpen(false)}
                    title="Tax forms"
                    fields={destinationFields ?? null}
                />
            </>
        );
    }

    //main UI
    return (
        <Box className={classes.tableWrap}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <SortedTableCell name="Submitted" field="submittedAt" sortBy={sortBy} changeSort={changeSort} />
                        <TableCell className={classes.tableHeaderCell} style={showCreatorSearch ? { padding: 0 } : {}}>
                            <div style={showCreatorSearch ? {
                                border: "1px solid #747474",
                            } : {}}>
                                <SearchField showSearch={showCreatorSearch} setShowSearch={setShowCreatorSearch} input={creatorUsername} setInput={setCreatorUsername} clearSearch={clearSearchInputInCollumn} applyFilters={applyFilters} />
                            </div>
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>
                            <UniversalDropdown
                                label="Type"
                                value={type}
                                options={typeOptions}
                                onChange={(val) => handleCloseDropdown("type", val, setType)}
                            />
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>
                            <UniversalDropdown
                                label="Status"
                                value={status}
                                options={statusOptions}
                                onChange={(val) => handleCloseDropdown("status", val, setStatus)}
                            />
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>File</TableCell>
                        <TableCell className={classes.tableHeaderCell} align="right">
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map(row => {
                        const canOpenRow = !!(row.fileId || row.mediaUrl);
                        const backgroundColor = row.status === "verified" ? "#85F96B0D" : row.status === "rejected" ? "#FF49770D" : ""
                        return (
                            <TableRow key={row._id} style={{ backgroundColor }}>
                                <TableCell className={classes.tableBodyCell}>
                                    {fmtDate(row.submittedAt || row.createdAt)}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell}>
                                    <RouterLink to={"/" + row.user.username} className={classes.userNameLink}>@{row.user.username}</RouterLink>
                                </TableCell>
                                <TableCell className={classes.tableBodyCell}>
                                    {row.type}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell}>
                                    <StatusChip status={row.status} />
                                </TableCell>
                                <TableCell className={clsx(classes.tableBodyCell, classes.ellipsisCell)}>
                                    {canOpenRow ? (
                                        <Link
                                            underline="always"
                                            onClick={() => previewFile(row)}
                                            style={{
                                                cursor: "pointer", color: "#fff", textDecoration: "underline"
                                            }}
                                        >
                                            {row.originalName || row.mimeType
                                                ? `${row.originalName || ""} (${row.mimeType || ""})`
                                                : "—"}
                                        </Link>

                                    ) : (
                                        <Typography>File not available</Typography>
                                    )}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    className={clsx(classes.tableBodyCell, classes.actionBtnRow)}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            setAnchorEl(e.currentTarget)
                                            openView(row._id)
                                        }}
                                        className={classes.outlinedIconButton}
                                    >
                                        <Eye size={16} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => openView(row._id)}
                                        className={classes.outlinedIconButton}
                                    >
                                        <Download size={16} />
                                    </IconButton>
                                    {row.status !== "verified" && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => doVerify(row._id)}
                                            className={clsx(classes.actionButtons, classes.verifyButton)}
                                        >
                                            <Check size={16} /> Verify
                                        </Button>
                                    )}
                                    {row.status === "pending" && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => doReject(row._id)}
                                            className={clsx(classes.actionButtons, classes.rejectButton)}
                                        >
                                            <X size={16} /> Reject
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {!loading && items.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={6}
                                style={{
                                    textAlign: "center",
                                    padding: 24,
                                    color: "rgba(255,255,255,0.6)",
                                }}
                            >
                                No tax forms found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* View meta dialog */}
            <DetailPopup title="Tax form" fields={destinationFields ?? null} viewOpen={viewOpen} setViewOpen={setViewOpen} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
        </Box>
    );
};

export default TaxFormsTable;
